import React from 'react';
import { RiAlertLine } from 'react-icons/ri';
import _, { isEmpty } from 'lodash';
import { getDateTime, getDateTimeAgo } from '@/utils/dateTime';
import { Cell, PillCell, ProgressPill } from '@/components/atoms';
import type { ProgressPillStatus } from '@/components/atoms';
import type { IVDITransactionProps } from '@/utils/types/vdiTransaction';
import type { ColumnDef } from '@/components/organism/Table';
import { GATEWAY_TRANSACTION_PROPS } from '@/constant/gatewayTransaction/gatewayTransaction.constant';
import { tid } from '@/utils/testId';
import { VDI_TRANSACTION_TEST_IDS as TID } from '@/constant/testIds/vdiTransaction.testIds';
import {
  EVDIRequestProcessStatus,
  EVdiVersion,
  getProviderConfigBasedOnPartnerId,
  getStatusOfEachStep,
  getStepperSteps,
  getVDIPartnerConfigKeysBasedOnSteps,
  VDI_PARTNER_CONFIG,
  VDI_RECONCILIATION_SUCCESS_CODE,
  VDI_TRANSACTION_SUCCESS_CODE,
} from './constant';

// ── Column builder ────────────────────────────────────────────────────────────
export const buildColumns = (
  isCopy: string,
  onCopyClick: (key: string) => void,
  providersListArray: any[],
): ColumnDef<IVDITransactionProps>[] => {
  const getRequestStatus = (item: IVDITransactionProps) => {
    const providerConfigurationData = getProviderConfigBasedOnPartnerId(
      item?.partnerId,
      providersListArray,
    );
    const stepperSteps = getStepperSteps(providerConfigurationData);
    const { hasPartnerStep, hasClientStep, hasReconciliation } = stepperSteps;
    const configKey = getVDIPartnerConfigKeysBasedOnSteps(
      hasPartnerStep,
      hasClientStep,
      hasReconciliation,
    );
    const config = (VDI_PARTNER_CONFIG as any)[configKey];
    if (!config)
      return {
        percentage: 0,
        status: EVDIRequestProcessStatus.FAILED,
        success: false,
      };

    const version = item.vdiVersion as EVdiVersion;
    const { type } = item;
    const checkpoints = config.checkpoints.filter(
      (cp: any) => cp.versions.includes(version) && cp.types.includes(type),
    );
    const totalSteps = checkpoints.length;
    let completedSteps = 0;
    let totalFailWeight = 0;
    const providerWithOnlyPartnerFlow = hasPartnerStep && !hasClientStep;
    const { ALL_STEPS_STATUS } = getStatusOfEachStep(item, providersListArray);
    const isPartnerSyncSuccess =
      ALL_STEPS_STATUS?.syncToPartnerStepStatus?.success;
    const newResponseFormat = !_.isUndefined((item?.meta as any)?.client);
    const clientObj =
      (item?.meta as any)?.client || (item?.meta as any)?.vdi_get_state;
    const clientGetStateSuccess = newResponseFormat
      ? (item?.meta as any)?.client?.get_state?.success
      : (item?.meta as any)?.vdi_get_state?.client;

    for (const checkpoint of checkpoints) {
      const value = checkpoint.path
        .split('.')
        .reduce((obj: any, key: string) => obj?.[key], item);
      const isCompleted =
        typeof value === 'boolean'
          ? value === true
          : value !== undefined && value !== null;
      const clientSuccess = newResponseFormat
        ? typeof clientObj?.success === 'boolean'
          ? clientObj.success
          : isCompleted
        : clientObj?.client;
      if (checkpoint.path === 'meta.client.get_state.success') {
        if (
          newResponseFormat
            ? providerWithOnlyPartnerFlow
              ? isPartnerSyncSuccess
              : clientGetStateSuccess
            : clientSuccess
        ) {
          completedSteps++;
        } else if (item?.statusCode !== 0) {
          totalFailWeight += -5;
        }
        break;
      } else if (!isCompleted) break;
      completedSteps++;
    }
    if (
      item?.statusCode !== VDI_TRANSACTION_SUCCESS_CODE ||
      item?.internalStatusCode !== VDI_TRANSACTION_SUCCESS_CODE
    ) {
      totalFailWeight += -5;
    }
    const basePercentage =
      totalSteps > 0 ? Math.round((completedSteps / totalSteps) * 100) : 0;
    const adjustedPercentage = Math.max(basePercentage + totalFailWeight, 0);
    const isProcessing =
      item?.statusCode === VDI_TRANSACTION_SUCCESS_CODE ||
      item?.statusCode === VDI_RECONCILIATION_SUCCESS_CODE ||
      (!item?.statusCode &&
        (item?.internalStatusCode === VDI_TRANSACTION_SUCCESS_CODE ||
          item?.internalStatusCode === VDI_RECONCILIATION_SUCCESS_CODE)) ||
      (providerWithOnlyPartnerFlow
        ? isPartnerSyncSuccess
        : clientGetStateSuccess);
    const status =
      isProcessing && adjustedPercentage === 100
        ? EVDIRequestProcessStatus.SUCCESS
        : isProcessing
          ? EVDIRequestProcessStatus.INPROGRESS
          : EVDIRequestProcessStatus.FAILED;
    return { percentage: adjustedPercentage, status, success: isProcessing };
  };

  return [
    {
      accessor: 'vdiTransactionId',
      header: GATEWAY_TRANSACTION_PROPS.VDITRANSACTION.COLUMNS.VDI_REQUEST_ID,
      render: (item, idx) => (
        <Cell
          testId={tid(TID.COL_VDI_REQUEST_ID(`${idx}`))}
          value={item?.vdiTransactionId}
          tooltip
          variant="mono"
          copy={{
            copyKey: `vdiId-${item?.vdiTransactionId}`,
            isCopy,
            onCopyClick,
          }}
        />
      ),
    },
    {
      accessor: 'operatorId',
      header: GATEWAY_TRANSACTION_PROPS.VDITRANSACTION.COLUMNS.OPERATOR_INFO,
      sorting: true,
      render: (item, idx) => (
        <div
          className="flex flex-col gap-0.5"
          data-testid={tid(TID.COL_OPERATOR_INFO(`${idx}`))}
        >
          <Cell
            value={item?.provider?.name}
            className="font-semibold max-w-[140px]"
          />
          {item?.partnerId && (
            <Cell value={item?.partnerId} className="max-w-[120px]" />
          )}
          {item?.operatorId && (
            <Cell
              value={item?.operatorId}
              size="text-[10px]"
              className="max-w-[200px]"
            />
          )}
        </div>
      ),
    },
    {
      accessor: 'type',
      header: GATEWAY_TRANSACTION_PROPS.VDITRANSACTION.COLUMNS.TYPE,
      sorting: true,
      render: (item, idx) => {
        const reconciliation =
          item?.meta &&
            typeof item.meta === 'object' &&
            'reconciliation' in item.meta
            ? (item.meta as any).reconciliation
            : undefined;
        return (
          <div
            className="flex flex-col gap-0.5"
            data-testid={tid(TID.COL_TYPE(`${idx}`))}
          >
            <Cell value={item?.type} className="font-semibold" />
            {reconciliation?.server && (
              <Cell
                value={`(${reconciliation.server})`}
                variant="mono"
                size="text-[9px]"
              />
            )}
          </div>
        );
      },
    },
    {
      accessor: 'statusCode',
      header: GATEWAY_TRANSACTION_PROPS.VDITRANSACTION.COLUMNS.STATUS_CODE,
      render: (item, idx) => {
        const code = item?.statusCode ?? item?.internalStatusCode;
        return (
          <PillCell
            testId={tid(TID.COL_STATUS_CODE(`${idx}`))}
            value={code}
            preset="statusCode"
            solid

            className="min-w-[3.5rem] justify-center tabular-nums"
          />
        );
      },
    },
    {
      accessor: 'transactionDtm',
      header: GATEWAY_TRANSACTION_PROPS.VDITRANSACTION.COLUMNS.TRANSACTION_DATE,
      sorting: true,
      render: (item, idx) => (
        <Cell
          testId={tid(TID.COL_TRANSACTION_DATE(`${idx}`))}
          value={item?.transactionDtm}
          format="datetime"
          variant="muted"
        />
      ),
    },
    {
      accessor: 'updatedOn',
      header: GATEWAY_TRANSACTION_PROPS.VDITRANSACTION.COLUMNS.LAST_UPDATED,
      sorting: true,
      render: (item, idx) => (
        <Cell
          testId={tid(TID.COL_UPDATED_ON(`${idx}`))}
          value={item?.updatedOn}
          variant="muted"
          tooltip={getDateTime(item?.updatedOn)}
          tooltipPlacement="top"
        >
          {item?.updatedOn ? getDateTimeAgo(item.updatedOn) : '—'}
        </Cell>
      ),
    },
    {
      accessor: 'createdOn',
      header: GATEWAY_TRANSACTION_PROPS.VDITRANSACTION.COLUMNS.CREATED_ON,
      sorting: true,
      render: (item, idx) => (
        <div className="flex flex-col gap-0.5">
          <Cell
            testId={tid(TID.COL_CREATED_ON(`${idx}`))}
            value={item?.createdOn}
            format="datetime"
            variant="muted"
          />
          <Cell
            value={item?.createdOn}
            size="text-[10px]"
            variant="muted"
          >
            {item?.createdOn ? getDateTimeAgo(item.createdOn) : '—'}
          </Cell>
        </div>
      ),
    },
    {
      accessor: 'vdiTransactionId' as keyof IVDITransactionProps,
      header: GATEWAY_TRANSACTION_PROPS.VDITRANSACTION.COLUMNS.REQUEST_STATUS,
      render: (item, idx) => {
        const hasConfig = getProviderConfigBasedOnPartnerId(
          item?.partnerId,
          providersListArray,
        );
        if (!hasConfig || isEmpty(hasConfig)) {
          return (
            <ProgressPill
              testId={tid(TID.COL_REQUEST_STATUS(`${idx}`))}
              percentage={0}
              status="failed"
              configMissing
            />
          );
        }
        const { percentage, status } = getRequestStatus(item);
        const pillStatus: ProgressPillStatus =
          status === EVDIRequestProcessStatus.SUCCESS
            ? 'success'
            : status === EVDIRequestProcessStatus.INPROGRESS
              ? 'inprogress'
              : 'failed';
        return (
          <ProgressPill
            testId={tid(TID.COL_REQUEST_STATUS(`${idx}`))}
            percentage={percentage}
            status={pillStatus}
          />
        );
      },
    },
  ];
};
